<script setup lang="ts">
import { Button } from '@dashhudson/dashing-ui/button';
import { Card } from '@dashhudson/dashing-ui/card';
import { ContentBanner } from '@dashhudson/dashing-ui/content-banner';
import { Divider } from '@dashhudson/dashing-ui/divider';
import { useDrawer } from '@dashhudson/dashing-ui/drawer';
import { RadioButton } from '@dashhudson/dashing-ui/radio-button';
import Formatters from '@dashhudson/dashing-ui/utils/formatters';
import { computed, onBeforeMount, ref, watch } from 'vue';

import { BUSINESS_HOURS } from '@/app/community/constants/inboxAutomation';
import type { BrandUser } from '@/app/community/types/user';
import { SETTINGS_DRAWERS } from '@/app/settings/constants/drawers';
import type { Weekday } from '@/app/settings/types/business-hours';
import { useBusinessHoursStore } from '@/stores/business-hours';
import { useCommunityInteractionRulesStore } from '@/stores/community-interaction-rules';
import { useCommunityMultiBrandsStore } from '@/stores/community-multi-brands';
import { useIdentityStore } from '@/stores/identity';
import { getUserDisplayName } from '@/utils/user';

interface Props {
  brandId: number;
  modelValue?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  modelValue: undefined,
});

interface Emits {
  (e: 'update:modelValue', value: boolean): void;
  (e: 'valid', value: boolean): void;
  (e: 'commandName', value: string): void;
  (e: 'shouldHideActions', value: boolean): void;
}

const emit = defineEmits<Emits>();

const ruleStore = useCommunityInteractionRulesStore();
const multiBrandStore = useCommunityMultiBrandsStore();
const identityStore = useIdentityStore();
const { launchDrawer } = useDrawer();

const offHoursTooltip = 'Any times outside the business hours set below';
const noHoursBannerMessage =
  'Apply actions based on whether messages were received during your brands Business Hours or Off-Hours.';
const businessHoursStore = useBusinessHoursStore();

const localValue = ref(props.modelValue ?? BUSINESS_HOURS.OFF_HOURS);

watch(
  () => props.modelValue,
  (newValue) => {
    localValue.value = newValue ?? BUSINESS_HOURS.OFF_HOURS;
  },
  { immediate: true },
);

watch(
  localValue,
  (newValue) => {
    emit('update:modelValue', newValue);
    emit('valid', newValue !== undefined);
  },
  { immediate: true },
);

const brandBusinessHours = computed(() => businessHoursStore.getBrandBusinessHours(props.brandId));

const hasBusinessHoursConfigured = computed(() =>
  businessHoursStore.hasBusinessHoursConfigured(props.brandId),
);

watch(
  hasBusinessHoursConfigured,
  (hasHours) => {
    emit('shouldHideActions', !hasHours);
  },
  { immediate: true },
);

const accessibleBrandUsers = computed<BrandUser[]>(
  () =>
    (multiBrandStore.brandIdToAccessibleBrandUsersMap as Record<number, BrandUser[]>)[
      props.brandId
    ] ?? [],
);

function getFormattedUserName(userId: number | null | undefined): string | null {
  if (!userId) return null;
  const user = accessibleBrandUsers.value.find((u) => u.id === userId);
  return user ? getUserDisplayName(user) : String(userId);
}

const formattedSchedule = computed(() => {
  const brandHours = brandBusinessHours.value;
  if (!brandHours?.hoursConfig) return null;

  const formatTime = (time: string): string => {
    const [hours, minutes] = time.split(':');
    const hour = parseInt(hours, 10);
    const ampm = hour >= 12 ? 'pm' : 'am';
    const displayHour = hour % 12 || 12;
    return `${displayHour}:${minutes} ${ampm}`;
  };

  const formatDate = (dateString: string | null | undefined): string | null => {
    if (!dateString) return null;
    const identity = identityStore.identity as { time_zone_name?: string } | null;
    return Formatters.formatDate(new Date(dateString), {
      timeZone: identity?.time_zone_name,
    });
  };

  const capitalize = (str: string): string => str.charAt(0).toUpperCase() + str.slice(1);

  const hoursConfig = brandHours.hoursConfig;
  const daysOfWeek: Weekday[] = [
    'monday',
    'tuesday',
    'wednesday',
    'thursday',
    'friday',
    'saturday',
    'sunday',
  ];

  return {
    timezone: brandHours.timezone?.replace('/', ' / ').replace(/_/g, ' '),
    days: daysOfWeek.map((day) => {
      const dayHours = hoursConfig[day];
      const dayName = capitalize(day);

      if (dayHours?.open && dayHours?.close) {
        return {
          day: dayName,
          hours: `${formatTime(dayHours.open)} - ${formatTime(dayHours.close)}`,
        };
      }
      return {
        day: dayName,
        hours: 'Closed',
      };
    }),
    createdBy: getFormattedUserName(brandHours.createdBy),
    createdAt: formatDate(brandHours.createdAt),
    updatedBy: getFormattedUserName(brandHours.updatedBy),
    updatedAt: formatDate(brandHours.updatedAt),
  };
});

function openBusinessHoursDrawer() {
  ruleStore.switchedToNestedDrawer = true;
  launchDrawer({ name: SETTINGS_DRAWERS.BUSINESS_HOURS });
}

onBeforeMount(() => {
  emit('commandName', 'Apply');
  emit('valid', localValue.value !== undefined);
});
</script>

<template>
  <div class="!w-[13.5rem]">
    <div v-if="hasBusinessHoursConfigured" class="dash-scrollbar-hidden flex flex-col gap-4">
      <!-- Selection -->
      <h4 class="dash-text-base dash-typo-base-semibold">Messages Received During</h4>
      <div class="flex flex-col gap-4">
        <RadioButton
          v-model="localValue"
          name="isDuringBusinessHours"
          :value="BUSINESS_HOURS.OFF_HOURS"
          label="Off-Hours"
          raw-label
          :tooltip="offHoursTooltip"
          input-id="off-hours"
        />
        <RadioButton
          v-model="localValue"
          name="isDuringBusinessHours"
          :value="BUSINESS_HOURS.DURING_BUSINESS_HOURS"
          label="Business Hours"
          raw-label
          input-id="business-hours"
        />
      </div>

      <!-- Business Hours Info Card -->
      <Card severity="flat" class="dash-bg-background-secondary-hover dash-rounded p-4">
        <div class="flex flex-col gap-3">
          <div class="flex items-center justify-between">
            <div class="dash-text-base dash-text-primary font-medium">Business Hours</div>
            <Button severity="utility" size="xs" icon="edit" @click="openBusinessHoursDrawer" />
          </div>
          <div v-if="formattedSchedule" class="flex flex-col gap-2">
            <!-- Timezone -->
            <div class="dash-text-base dash-text-subtle">
              {{ formattedSchedule.timezone }}
            </div>
            <!-- Days Grid -->
            <div class="flex flex-col gap-2">
              <div
                v-for="dayInfo in formattedSchedule.days"
                :key="dayInfo.day"
                class="flex justify-between"
              >
                <div class="dash-text-base dash-text-primary">{{ dayInfo.day }}</div>
                <div class="dash-text-base dash-text-subtle">
                  {{ dayInfo.hours }}
                </div>
              </div>
            </div>

            <Divider />

            <!-- Metadata -->
            <div class="flex flex-col">
              <div class="dash-text-base dash-text-subtle">
                Created by {{ formattedSchedule.createdBy }} on {{ formattedSchedule.createdAt }}
              </div>
              <div v-if="formattedSchedule.updatedBy" class="dash-text-base dash-text-subtle mt-2">
                Updated by {{ formattedSchedule.updatedBy }} on {{ formattedSchedule.updatedAt }}
              </div>
            </div>
          </div>
        </div>
      </Card>
    </div>
    <div v-else>
      <ContentBanner
        variant="sectionEmpty"
        :message="noHoursBannerMessage"
        icon="clock"
        :cta-button="{ label: 'Add Business Hours' }"
        @click="openBusinessHoursDrawer"
      >
      </ContentBanner>
    </div>
  </div>
</template>
